/** Тонкий клиент к леджеру. Нарушение правила кода приходит как 409 с `overridable`. */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly rule?: string,
    readonly overridable?: boolean
  ) {
    super(message);
  }
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const text = await res.text();
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok) {
    throw new ApiError(
      (data.error as string) ?? `Ошибка ${res.status}`,
      res.status,
      data.rule as string | undefined,
      data.overridable as boolean | undefined
    );
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => call<T>("GET", url),
  post: <T>(url: string, body?: unknown) => call<T>("POST", url, body ?? {}),
  patch: <T>(url: string, body: unknown) => call<T>("PATCH", url, body),
  del: <T>(url: string) => call<T>("DELETE", url)
};

export type Adherence = { planned: number; done: number; pct: number | null; weeks: number };

export type Goal = {
  id: string;
  title: string;
  why: string | null;
  goal_type: string;
  horizon_bucket: string;
  domain: string;
  target_date: string | null;
  success_criterion: string | null;
  proof_rule: string | null;
  machine_rule: string | null;
  parent_goal_id: string | null;
  chain_declared_at: string | null;
  criteria_locked_at: string | null;
  status: string;
  cooling_until: string | null;
  reward: string | null;
  reward_unlocked_at: string | null;
  days_since_touch?: number | null;
};

export type Protocol = {
  id: string;
  hypothesis: string;
  metric_name: string;
  metric_unit: string | null;
  target_value: number | null;
  baseline_value: number | null;
  success_rule: string;
  preregistered_at: string | null;
  status: string;
  window_start: string;
  window_end: string;
  outcome: string | null;
  /** Ссылка на предшественника: смена ритма — это новый эксперимент, а не правка старого. */
  supersedes_id: string | null;
  adherence: Adherence;
};

export type Touch = {
  id: string;
  occurred_at: string;
  kind: string;
  obstacle_code: string;
  effort: number | null;
  metric_value: number | null;
};

export type Forecast = {
  id: string;
  kind: string;
  prob: number | null;
  date_p50: string | null;
  made_at: string;
  resolve_by: string;
  resolution: string | null;
};

export type RuleClause = {
  text: string;
  status: "covered" | "missing" | "contradicted";
  evidence_index: number | null;
  note: string;
};

export type Verdict = {
  id: string;
  decision: string;
  judged_by: string;
  model: string | null;
  reasoning: string | null;
  missing: string | null;
  rule_snapshot: string;
  judged_at: string;
  appealed: number;
  appeal_reason: string | null;
  final_decision: string | null;
};

/** Карточка предложения агента. В леджер попадает только нажатием владельца. */
export type Proposal = {
  id: string;
  kind: "goal" | "protocol" | "criterion_change" | "method_match";
  rationale: string | null;
  subject_id: string | null;
  created_at: string;
  parsed: Record<string, unknown>;
};

export type CorpusGap = { id: string; query: string; reason: string | null; noted_at: string };

/** Право агента называть свою вероятность. Считает сервер, не модель. */
export type OracleGate = {
  allowed: boolean;
  resolved_n: number;
  threshold: number;
  remaining: number;
  reason: string;
};

/** Пункты правила из снимка вердикта: они замёрзли в момент суда. */
export function clausesOf(verdict: Verdict): RuleClause[] {
  try {
    const snapshot = JSON.parse(verdict.rule_snapshot) as { clauses?: RuleClause[] };
    return snapshot.clauses ?? [];
  } catch {
    return [];
  }
}

export type GoalView = {
  goal: Goal;
  protocols: Protocol[];
  touches: Touch[];
  forecasts: Forecast[];
  verdicts: Verdict[];
  obstacles: { obstacle_code: string; n: number }[];
  recovery: { n: number; median_days: number | null; without_return: number };
  days_since_touch: number | null;
  /** Цепочка: длинная цель сверху и короткие звенья снизу. Уровень ровно один. */
  chain: { parent: Goal | null; children: Goal[] };
  revisions: { at: string; action: string; after: string | null }[];
};

export type Today = {
  today: string;
  tz: string;
  steps: {
    goal_id: string;
    goal_title: string;
    target_date: string | null;
    protocol_id: string;
    metric_name: string;
    metric_unit: string | null;
    adherence: Adherence;
  }[];
  /** Протокол с закрывшимся окном: не шаг, а решение. Ждёт исхода, см. Today.tsx. */
  decisions: {
    /**
     * `outcome` — нужен исход, один тап. `review` — исход есть, нужен разбор.
     * `supersede` — разбор кончился решением «Меняю», а преемника нет.
     */
    stage: "outcome" | "review" | "supersede";
    goal_id: string;
    goal_title: string;
    protocol_id: string;
    hypothesis: string;
    success_rule: string;
    window_start: string;
    window_end: string;
    outcome: string | null;
    cadence: { kind: string; value?: number; days?: number[] };
    adherence: Adherence;
  }[];
  /** Просевшая приверженность у идущего протокола. Порог тот же, что у бота. */
  drift: {
    goal_id: string;
    goal_title: string;
    protocol_id: string;
    hypothesis: string;
    threshold_pct: number;
    adherence: Adherence;
  }[];
  /** Ожидающие напоминания: и заведённые владельцем, и системные. */
  reminders: {
    id: string;
    kind: string;
    subject_ref: string | null;
    at: string;
    message: string;
    created_by: string;
  }[];
  silence: { goal_id: string; title: string; days: number }[];
  cooling: { id: string; title: string; cooling_until: string | null }[];
  forecasts_resolved: number;
  protocols: { running: number; limit: number };
};

export const OBSTACLES = [
  ["none", "ничего"],
  ["time", "время"],
  ["energy", "силы"],
  ["attention", "внимание"],
  ["mood", "настроение"],
  ["external", "внешнее"],
  ["unclear_next_step", "неясен шаг"],
  ["skill_gap", "навык"],
  ["social", "люди"],
  ["environment", "обстановка"],
  ["health", "здоровье"]
] as const;

/** Подписи словарей. Значения в базе остаются enum'ами — по ним строится референсный класс. */
export const LABEL: Record<string, string> = {
  // статусы
  candidate: "кандидат", cooling: "отлёжка", active: "активна", shelved: "отложена",
  achieved: "достигнута", abandoned: "закрыта", expired: "просрочена",
  // исходы протокола: «не сработало» и «не выполнялось» — разные утверждения
  worked: "сработало", unclear: "неясно", did_not_work: "не сработало",
  not_executed: "не выполнялось",
  // вердикты и пункты правила
  accepted: "принято", rejected: "отклонено", needs_more: "не хватает доказательств",
  covered: "закрыт", missing: "не закрыт", contradicted: "опровергнут",
  code: "код", agent: "агент", owner: "владелец",
  // виды предложений
  goal: "цель", protocol: "протокол", criterion_change: "уточнение критерия",
  method_match: "методика к протоколу",
  // типы целей. Жили отдельным списком в форме новой цели, пока подписи требовались
  // только ей; карточка предложения агента показала бы «project» — машинный ключ
  // в тексте для владельца. Словарь один на все экраны по той же причине, по которой
  // один порог у бота и у экрана: разъехавшись, они начинают противоречить друг другу.
  habit: "привычка", project: "проект", skill: "навык", quit: "бросить",
  performance: "результат", exploration: "поиск",
  // Виды касания. «Пропуск» и «сорвалось» — разные вещи: первое про меня, второе
  // про обстоятельства, и смешивать их нельзя ровно по той же причине, по которой
  // «не сработало» отделено от «не выполнялось».
  planning: "планирование", recovery: "возврат", skipped: "пропуск", blocked: "сорвалось",
  // горизонты и сферы
  week: "неделя", month: "месяц", quarter: "квартал", year_plus: "год+",
  work: "работа", sport: "спорт", family: "семья", personal: "личное", study: "учёба",
  // препятствия
  none: "ничего", time: "время", energy: "силы", attention: "внимание", mood: "настроение",
  external: "внешнее", unclear_next_step: "неясен шаг", skill_gap: "навык", social: "люди",
  environment: "обстановка", health: "здоровье"
};

export const label = (key: string): string => LABEL[key] ?? key;

/* ── Оракул ────────────────────────────────────────────────────────────────
   Экран собирается ОДНИМ запросом и целиком считается кодом: ни одного вызова
   модели, поэтому он работает и без ANTHROPIC_API_KEY. */

export type ForecastRow = {
  id: string;
  subject_id: string;
  subject_title: string | null;
  question: string;
  kind: string;
  forecaster: string;
  prob: number | null;
  date_p50: string | null;
  made_at: string;
  resolve_by: string;
  resolution: string | null;
  resolved_at: string | null;
  brier: number | null;
};

export type PremortemItem = {
  id: string;
  text: string;
  obstacle_code: string;
  source: string;
  method_slug: string | null;
};

export type Oracle = {
  gate: OracleGate;
  calibration: {
    owner: { binary: { n: number; brier: number | null; overconfidence: number | null } };
    agent: { binary: { n: number; brier: number | null; overconfidence: number | null } } | null;
  };
  open_forecasts: ForecastRow[];
  resolved_forecasts: ForecastRow[];
  verdicts: {
    id: string;
    goal_title: string | null;
    decision: string;
    final_decision: string | null;
    judged_by: string;
    missing: string | null;
  }[];
  premortem_goals: { id: string; title: string }[];
  premortem: {
    goal: { id: string; title: string };
    checkable: { machine: boolean; locked_at: string | null };
    items: PremortemItem[];
    typical: { obstacle_code: string; methods: { slug: string; title: string }[] }[];
  } | null;
};

/* ── Разбор (AAR) ──────────────────────────────────────────────────────────
   Первые два вопроса заполняет код из леджера: «берётся из записи протокола,
   а не из памяти» и «только факты и числа из ленты касаний» — правила карточки
   corpus/methods/after-action-review.md. Модель к разбору не подключена. */

export const REVIEW_DECISIONS = [
  ["continue", "Продолжаю"],
  ["adjust", "Меняю"],
  ["stop", "Прекращаю"],
  ["archive", "В архив"],
  ["new_goal", "Новая цель"]
] as const;

export type PremortemCheck = {
  id: string;
  text: string;
  obstacle_code: string;
  source: string;
  method_slug: string | null;
  seen_in_touches: number;
  suggested: boolean;
  confirmed: boolean | null;
};

export type ReviewPrefill = {
  protocol_id: string;
  goal_id: string;
  period_start: string;
  period_end: string;
  q_intended: string;
  q_happened: string;
  premortem: PremortemCheck[];
  blind_spots: { obstacle_code: string; n: number }[];
};
